import Modal from '@mui/material/Modal'
import Webcam from 'react-webcam'
import Box from '@mui/material/Box'
import { useEffect, useRef, useState } from 'react'
import { Button, IconButton, TextField, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faArrowLeft,
    faBolt,
    faCamera,
    faCircleNotch,
    faPaintBrush,
    faCircle,
    faSquare,
    faTrash,
    faArrowRight
} from '@fortawesome/free-solid-svg-icons'
import { useAlertContext } from '../../context'

// Distance computation helpers for arrow rotation and handle grab detection
const getDistance = (x1, y1, x2, y2) => {
    return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2))
}

const getDistanceToSegment = (px, py, x1, y1, x2, y2) => {
    const l2 = Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2)
    if (l2 === 0) return getDistance(px, py, x1, y1)
    let t = ((px - x1) * (x2 - x1) + (py - y1) * (y2 - y1)) / l2
    t = Math.max(0, Math.min(1, t))
    return getDistance(px, py, x1 + t * (x2 - x1), y1 + t * (y2 - y1))
}

const CameraModal = (props) => {
    const { open, handleClose, onImageCapture, initialImageSrc } = props
    const webcamRef = useRef(null)
    const canvasRef = useRef(null)
    const [imageSrc, setImageSrc] = useState(initialImageSrc || null)
    const facingMode = 'environment'
    const [torchOn, setTorchOn] = useState(false)
    const loadedImgRef = useRef(null)

    // Context for custom ICV alert confirmation dialog
    const { setConfirm } = useAlertContext() || {}

    // Canvas drawing states
    const [drawingTool, setDrawingTool] = useState(null) // default null (no tool selected)
    const [isDrawing, setIsDrawing] = useState(false)
    const [startPos, setStartPos] = useState({ x: 0, y: 0 })
    const [savedImageData, setSavedImageData] = useState(null)
    const [imageComment, setImageComment] = useState('')
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 })

    // Interactive shape selection states
    const [activeShape, setActiveShape] = useState(null)
    const [isMovingShape, setIsMovingShape] = useState(false)
    const [activeHandle, setActiveHandle] = useState(null) // 'body', 'x1y1', 'x2y2', or null
    const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 })
    const [shapeStartCoords, setShapeStartCoords] = useState(null)
    const [menuOpen, setMenuOpen] = useState(false)
    const [history, setHistory] = useState([])

    const videoConstraints = {
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        facingMode
    }

    const setTorchMode = async (enabled) => {
        if (!webcamRef.current?.stream) return

        const track = webcamRef.current.stream.getVideoTracks()[0]

        try {
            await track.applyConstraints({
                advanced: [{
                    torch: enabled,
                    exposureMode: 'continuous',
                    exposureCompensation: 0
                }]
            })
        } catch (error) {
            console.log('Error configuring flash:', error)
        }
    }

    const toggleTorch = async () => {
        const newTorchState = !torchOn
        setTorchOn(newTorchState)
        await setTorchMode(newTorchState)
    }

    const handleCapture = async () => {
        const videoElement = webcamRef.current?.video
        const canvas = document.createElement('canvas')

        if (videoElement) {
            try {
                if (torchOn) {
                    await setTorchMode(true)
                }

                const width = videoElement.videoWidth
                const height = videoElement.videoHeight
                canvas.width = width
                canvas.height = height

                const context = canvas.getContext('2d')
                context.drawImage(videoElement, 0, 0, width, height)

                const imgBase64 = canvas.toDataURL('image/jpeg', 0.8)
                setImageSrc(imgBase64)

                if (torchOn) {
                    await new Promise((resolve) => setTimeout(resolve, 2000))
                    await setTorchMode(false)
                }
            } catch (err) {
                console.log('Capture error:', err)
                if (torchOn) {
                    await setTorchMode(false)
                }
            }
        }
    }

    // Initialize with initial image if provided, or reset states when modal visibility changes
    useEffect(() => {
        if (open) {
            setImageSrc(initialImageSrc || null)
            setImageComment('')
            setMenuOpen(false)
            setDrawingTool(null)
        } else {
            setImageSrc(null)
            if (torchOn) {
                setTorchMode(false)
                setTorchOn(false)
            }
        }
    }, [open, initialImageSrc])

    // Load image element when imageSrc changes
    useEffect(() => {
        if (!imageSrc) {
            loadedImgRef.current = null
            setCanvasDimensions({ width: 0, height: 0 })
            return
        }

        const img = new Image()
        img.src = imageSrc
        img.onload = () => {
            loadedImgRef.current = img
            setCanvasDimensions({ width: img.width, height: img.height })
        }
    }, [imageSrc])

    // Draw image onto Canvas after DOM element and dimensions are updated
    useEffect(() => {
        if (!imageSrc || !loadedImgRef.current || canvasDimensions.width === 0 || canvasDimensions.height === 0) return

        const canvas = canvasRef.current
        if (canvas) {
            const ctx = canvas.getContext('2d')
            ctx.drawImage(loadedImgRef.current, 0, 0, canvasDimensions.width, canvasDimensions.height)
            const initialData = ctx.getImageData(0, 0, canvasDimensions.width, canvasDimensions.height)
            setHistory([initialData])
            setActiveShape(null)
        }
    }, [canvasDimensions, imageSrc])

    // Helper to calculate proportional stroke width
    const getStrokeWidth = (canvas) => {
        return Math.max(3, Math.round((canvas?.width || 1000) / 200))
    }

    // Helper to draw a shape (circle, rectangle, arrow)
    const drawShape = (ctx, shape, isActive) => {
        if (!shape) return

        const canvas = canvasRef.current
        const strokeW = getStrokeWidth(canvas)
        ctx.strokeStyle = '#ff1744'
        ctx.lineWidth = strokeW
        ctx.setLineDash([])

        if (shape.type === 'circle') {
            ctx.beginPath()
            const cx = shape.x1
            const cy = shape.y1
            const radius = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2))
            ctx.arc(cx, cy, radius, 0, 2 * Math.PI)
            ctx.stroke()

            if (isActive) {
                // Dashed blue bounding box
                ctx.strokeStyle = '#2196f3'
                ctx.lineWidth = Math.max(1.5, strokeW / 4)
                ctx.setLineDash([4, 4])
                ctx.beginPath()
                ctx.rect(cx - radius - 4, cy - radius - 4, radius * 2 + 8, radius * 2 + 8)
                ctx.stroke()
                ctx.setLineDash([])
            }
        } else if (shape.type === 'rect') {
            ctx.beginPath()
            ctx.rect(shape.x1, shape.y1, shape.x2 - shape.x1, shape.y2 - shape.y1)
            ctx.stroke()

            if (isActive) {
                // Dashed blue bounding box
                ctx.strokeStyle = '#2196f3'
                ctx.lineWidth = Math.max(1.5, strokeW / 4)
                ctx.setLineDash([4, 4])
                ctx.beginPath()
                const minX = Math.min(shape.x1, shape.x2)
                const maxX = Math.max(shape.x1, shape.x2)
                const minY = Math.min(shape.y1, shape.y2)
                const maxY = Math.max(shape.y1, shape.y2)
                ctx.rect(minX - 4, minY - 4, (maxX - minX) + 8, (maxY - minY) + 8)
                ctx.stroke()
                ctx.setLineDash([])
            }
        } else if (shape.type === 'arrow') {
            ctx.beginPath()
            ctx.moveTo(shape.x1, shape.y1)
            ctx.lineTo(shape.x2, shape.y2)
            ctx.stroke()

            const angle = Math.atan2(shape.y2 - shape.y1, shape.x2 - shape.x1)
            const headlen = Math.max(12, strokeW * 3.5)

            ctx.beginPath()
            ctx.moveTo(shape.x2, shape.y2)
            ctx.lineTo(shape.x2 - headlen * Math.cos(angle - Math.PI / 6), shape.y2 - headlen * Math.sin(angle - Math.PI / 6))
            ctx.moveTo(shape.x2, shape.y2)
            ctx.lineTo(shape.x2 - headlen * Math.cos(angle + Math.PI / 6), shape.y2 - headlen * Math.sin(angle + Math.PI / 6))
            ctx.stroke()
        }

        // Draw handles at endpoints for active shape (allows resize and rotate)
        if (isActive) {
            const handleRadius = Math.max(6, Math.round(strokeW * 1.3))

            // Handle 1 (start: x1, y1)
            ctx.beginPath()
            ctx.arc(shape.x1, shape.y1, handleRadius, 0, 2 * Math.PI)
            ctx.fillStyle = '#ffffff'
            ctx.fill()
            ctx.strokeStyle = '#2196f3'
            ctx.lineWidth = Math.max(2, strokeW / 3)
            ctx.stroke()

            // Handle 2 (end: x2, y2)
            ctx.beginPath()
            ctx.arc(shape.x2, shape.y2, handleRadius, 0, 2 * Math.PI)
            ctx.fillStyle = '#ffffff'
            ctx.fill()
            ctx.strokeStyle = '#2196f3'
            ctx.lineWidth = Math.max(2, strokeW / 3)
            ctx.stroke()
        }
    }

    // Helper to calculate screen-to-canvas scale factor
    const getScaleFactor = () => {
        const canvas = canvasRef.current
        if (!canvas) return 1
        const rect = canvas.getBoundingClientRect()
        return canvas.width / rect.width
    }

    // Helper to check if coordinates are inside the shape (with screen-proportional padding)
    const isPointInsideShape = (x, y, shape) => {
        if (!shape) return false

        const scale = getScaleFactor()
        const padding = 15 * scale // 15 screen pixels scaled to canvas pixels

        if (shape.type === 'circle') {
            const cx = shape.x1
            const cy = shape.y1
            const radius = Math.sqrt(Math.pow(shape.x2 - shape.x1, 2) + Math.pow(shape.y2 - shape.y1, 2))
            const dist = Math.sqrt(Math.pow(x - cx, 2) + Math.pow(y - cy, 2))
            return dist <= radius + padding
        }

        if (shape.type === 'rect') {
            const minX = Math.min(shape.x1, shape.x2)
            const maxX = Math.max(shape.x1, shape.x2)
            const minY = Math.min(shape.y1, shape.y2)
            const maxY = Math.max(shape.y1, shape.y2)
            return x >= minX - padding && x <= maxX + padding && y >= minY - padding && y <= maxY + padding
        }

        return false
    }

    // Canvas drawing coordinates helper
    const getCoordinates = (e) => {
        const canvas = canvasRef.current
        if (!canvas) return { x: 0, y: 0 }

        const rect = canvas.getBoundingClientRect()
        let clientX, clientY

        if (e.touches && e.touches.length > 0) {
            clientX = e.touches[0].clientX
            clientY = e.touches[0].clientY
        } else if (e.changedTouches && e.changedTouches.length > 0) {
            clientX = e.changedTouches[0].clientX
            clientY = e.changedTouches[0].clientY
        } else {
            clientX = e.clientX
            clientY = e.clientY
        }

        // Map screen client coordinates to original canvas coordinate space
        return {
            x: ((clientX - rect.left) / rect.width) * canvas.width,
            y: ((clientY - rect.top) / rect.height) * canvas.height
        }
    }

    const handleStartDraw = (e) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const pos = getCoordinates(e)

        // Check if grabbing an already drawn shape's handles or body to move/resize/rotate it
        if (activeShape) {
            const scale = getScaleFactor()
            const padding = 20 * scale // click boundary radius in canvas pixels

            // Check handle 1 (x1, y1)
            const dist1 = getDistance(pos.x, pos.y, activeShape.x1, activeShape.y1)
            if (dist1 < padding) {
                setIsMovingShape(true)
                setActiveHandle('x1y1')
                setDragStartPos(pos)
                setShapeStartCoords({ ...activeShape })
                return
            }

            // Check handle 2 (x2, y2)
            const dist2 = getDistance(pos.x, pos.y, activeShape.x2, activeShape.y2)
            if (dist2 < padding) {
                setIsMovingShape(true)
                setActiveHandle('x2y2')
                setDragStartPos(pos)
                setShapeStartCoords({ ...activeShape })
                return
            }

            // Check body selection
            let isInsideBody = false
            if (activeShape.type === 'arrow') {
                const distLine = getDistanceToSegment(pos.x, pos.y, activeShape.x1, activeShape.y1, activeShape.x2, activeShape.y2)
                isInsideBody = distLine < padding
            } else {
                isInsideBody = isPointInsideShape(pos.x, pos.y, activeShape)
            }

            if (isInsideBody) {
                setIsMovingShape(true)
                setActiveHandle('body')
                setDragStartPos(pos)
                setShapeStartCoords({ ...activeShape })
                return
            }

            // Clicked outside, stamp the shape!
            stampActiveShape()
        }

        // If no tool is active, do not start drawing new lines/shapes
        if (!drawingTool) return

        setIsDrawing(true)
        setStartPos(pos)

        const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        setSavedImageData(imgData)

        if (drawingTool === 'free') {
            ctx.beginPath()
            ctx.moveTo(pos.x, pos.y)
            ctx.strokeStyle = '#ff1744'
            ctx.lineWidth = getStrokeWidth(canvas)
            ctx.lineCap = 'round'
            ctx.lineJoin = 'round'
        }
    }

    const handleDrawing = (e) => {
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')
        const pos = getCoordinates(e)

        if (isMovingShape && activeShape && activeHandle) {
            if (e.cancelable) e.preventDefault()
            const dx = pos.x - dragStartPos.x
            const dy = pos.y - dragStartPos.y

            const updatedShape = { ...activeShape }

            if (activeHandle === 'body') {
                updatedShape.x1 = shapeStartCoords.x1 + dx
                updatedShape.y1 = shapeStartCoords.y1 + dy
                updatedShape.x2 = shapeStartCoords.x2 + dx
                updatedShape.y2 = shapeStartCoords.y2 + dy
            } else if (activeHandle === 'x1y1') {
                updatedShape.x1 = shapeStartCoords.x1 + dx
                updatedShape.y1 = shapeStartCoords.y1 + dy
            } else if (activeHandle === 'x2y2') {
                updatedShape.x2 = shapeStartCoords.x2 + dx
                updatedShape.y2 = shapeStartCoords.y2 + dy
            }

            ctx.putImageData(history[history.length - 1], 0, 0)
            drawShape(ctx, updatedShape, true)
            setActiveShape(updatedShape)
            return
        }

        if (isDrawing) {
            if (e.cancelable) e.preventDefault()
            const strokeW = getStrokeWidth(canvas)
            if (drawingTool === 'free') {
                ctx.lineTo(pos.x, pos.y)
                ctx.stroke()
            } else if (drawingTool === 'circle' && savedImageData) {
                ctx.putImageData(savedImageData, 0, 0)
                ctx.beginPath()
                const radius = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2))
                ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI)
                ctx.strokeStyle = '#ff1744'
                ctx.lineWidth = strokeW
                ctx.stroke()
            } else if (drawingTool === 'rect' && savedImageData) {
                ctx.putImageData(savedImageData, 0, 0)
                ctx.beginPath()
                ctx.rect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y)
                ctx.strokeStyle = '#ff1744'
                ctx.lineWidth = strokeW
                ctx.stroke()
            } else if (drawingTool === 'arrow' && savedImageData) {
                ctx.putImageData(savedImageData, 0, 0)
                // Draw live arrow shaft
                ctx.beginPath()
                ctx.moveTo(startPos.x, startPos.y)
                ctx.lineTo(pos.x, pos.y)
                ctx.strokeStyle = '#ff1744'
                ctx.lineWidth = strokeW
                ctx.stroke()

                // Draw live arrowhead
                const angle = Math.atan2(pos.y - startPos.y, pos.x - startPos.x)
                const headlen = Math.max(12, strokeW * 3.5)

                ctx.beginPath()
                ctx.moveTo(pos.x, pos.y)
                ctx.lineTo(pos.x - headlen * Math.cos(angle - Math.PI / 6), pos.y - headlen * Math.sin(angle - Math.PI / 6))
                ctx.moveTo(pos.x, pos.y)
                ctx.lineTo(pos.x - headlen * Math.cos(angle + Math.PI / 6), pos.y - headlen * Math.sin(angle + Math.PI / 6))
                ctx.stroke()
            }
        }
    }

    const handleEndDraw = (e) => {
        if (isMovingShape) {
            setIsMovingShape(false)
            setActiveHandle(null)
            return
        }

        if (isDrawing) {
            setIsDrawing(false)
            const canvas = canvasRef.current
            if (!canvas) return
            const ctx = canvas.getContext('2d')

            if (drawingTool === 'free') {
                const currentData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                setHistory(prev => [...prev, currentData])
            } else {
                const pos = getCoordinates(e)
                const distance = Math.sqrt(Math.pow(pos.x - startPos.x, 2) + Math.pow(pos.y - startPos.y, 2))

                if (distance > 5) {
                    const newShape = {
                        type: drawingTool,
                        x1: startPos.x,
                        y1: startPos.y,
                        x2: pos.x,
                        y2: pos.y
                    }
                    setActiveShape(newShape)
                    ctx.putImageData(history[history.length - 1], 0, 0)
                    drawShape(ctx, newShape, true)
                } else {
                    ctx.putImageData(history[history.length - 1], 0, 0)
                }
            }
            setSavedImageData(null)
        }
    }

    const stampActiveShape = () => {
        if (!activeShape) return
        const canvas = canvasRef.current
        if (!canvas) return
        const ctx = canvas.getContext('2d')

        ctx.putImageData(history[history.length - 1], 0, 0)
        drawShape(ctx, activeShape, false)

        const currentData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        setHistory(prev => [...prev, currentData])
        setActiveShape(null)
    }

    const handleUndo = () => {
        if (history.length > 1) {
            const newHistory = history.slice(0, -1)
            setHistory(newHistory)
            const prevState = newHistory[newHistory.length - 1]

            const canvas = canvasRef.current
            if (canvas && prevState) {
                const ctx = canvas.getContext('2d')
                ctx.putImageData(prevState, 0, 0)
            }
        }
    }

    const handleResetCanvas = () => {
        const canvas = canvasRef.current
        if (canvas) {
            const ctx = canvas.getContext('2d')
            if (history.length > 0) {
                ctx.putImageData(history[0], 0, 0)
                setHistory([history[0]])
            }
        }
    }

    const handleSaveEditedImage = () => {
        const canvas = canvasRef.current
        if (canvas) {
            const ctx = canvas.getContext('2d')

            if (activeShape) {
                ctx.putImageData(history[history.length - 1], 0, 0)
                drawShape(ctx, activeShape, false)
            }

            const editedBase64 = canvas.toDataURL('image/jpeg', 0.8)
            onImageCapture(editedBase64, imageComment)
            setImageComment('')
            setActiveShape(null)
            handleClose()
        }
    }

    const handleRetakeClick = () => {
        const hasChanges = history.length > 1 || activeShape !== null || imageComment.trim() !== ''

        if (hasChanges) {
            if (setConfirm) {
                setConfirm(
                    'Confirmar Acción',
                    '¿Desea descartar los cambios y la imagen actual?',
                    () => {
                        if (initialImageSrc) {
                            handleClose()
                        } else {
                            setImageSrc(null)
                        }
                    }
                )
            } else {
                if (window.confirm('¿Desea descartar los cambios y la imagen actual?')) {
                    if (initialImageSrc) {
                        handleClose()
                    } else {
                        setImageSrc(null)
                    }
                }
            }
        } else {
            if (initialImageSrc) {
                handleClose()
            } else {
                setImageSrc(null)
            }
        }
    }

    const selectTool = (tool) => {
        if (activeShape) {
            stampActiveShape()
        }
        if (drawingTool === tool) {
            setDrawingTool(null) // toggle off
        } else {
            setDrawingTool(tool) // select tool
        }
    }

    const isEditingActive = drawingTool !== null

    return (
        <Modal
            open={open}
            onClose={handleClose}
        >
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '100vw',
                    height: '100vh',
                    bgcolor: '#0c0c0c',
                    color: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif'
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {imageSrc ? (
                    // IMAGE EDITOR VIEW (FLOAT OVERLAY LAYOUT)
                    <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                        {/* Floating Header */}
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 5,
                            display: 'flex',
                            alignItems: 'center',
                            p: 2,
                            background: 'linear-gradient(to bottom, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0) 100%)'
                        }}>
                            <IconButton onClick={handleRetakeClick} sx={{ color: '#fff', mr: 2, bgcolor: 'rgba(0,0,0,0.4)', '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' } }}>
                                <FontAwesomeIcon icon={faArrowLeft} />
                            </IconButton>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
                                Editor de Imagen
                            </Typography>
                        </Box>

                        {/* Full Screen Workspace for Canvas */}
                        <Box sx={{
                            flex: 1,
                            minHeight: 0,
                            minWidth: 0,
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            p: { xs: 1, sm: 2, md: 3 },
                            pt: { xs: '65px', md: '70px' },
                            pb: { xs: '200px', md: '135px' }, // leave space for bottom floating card
                            overflow: 'hidden',
                            boxSizing: 'border-box'
                        }}>
                            <canvas
                                ref={canvasRef}
                                width={canvasDimensions.width}
                                height={canvasDimensions.height}
                                onMouseDown={handleStartDraw}
                                onMouseMove={handleDrawing}
                                onMouseUp={handleEndDraw}
                                onMouseLeave={handleEndDraw}
                                onTouchStart={handleStartDraw}
                                onTouchMove={handleDrawing}
                                onTouchEnd={handleEndDraw}
                                style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    touchAction: 'none',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                                    borderRadius: '8px',
                                    cursor: activeShape ? 'move' : (drawingTool === 'free' ? 'crosshair' : 'default'),
                                    backgroundColor: '#000000',
                                    display: 'block'
                                }}
                            />
                        </Box>

                        {/* Floating Drawing Tool Menu & Retake/Discard Button (Upper Right) */}
                        <Box sx={{
                            position: 'absolute',
                            top: '75px',
                            right: '15px',
                            zIndex: 5,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 1.5
                        }}>
                            {/* Pencil / Draw Tool Toggle */}
                            <IconButton
                                onClick={() => setMenuOpen(!menuOpen)}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: isEditingActive ? '#ff1744' : 'rgba(255, 255, 255, 0.15)',
                                    color: '#fff',
                                    border: isEditingActive ? 'none' : '1px solid rgba(255, 255, 255, 0.25)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                    '&:hover': { bgcolor: isEditingActive ? '#b71c1c' : 'rgba(255, 255, 255, 0.25)' }
                                }}
                            >
                                <FontAwesomeIcon icon={faPaintBrush} />
                            </IconButton>

                            {/* Re-tomar / Descartar Button */}
                            <IconButton
                                onClick={handleRetakeClick}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    bgcolor: 'rgba(255, 255, 255, 0.15)',
                                    color: '#ff5252',
                                    border: '1px solid rgba(255, 255, 255, 0.25)',
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
                                    '&:hover': { bgcolor: '#ff5252', color: '#fff' }
                                }}
                                title={initialImageSrc ? 'Descartar Foto' : 'Re-tomar Foto'}
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </IconButton>

                            {/* Submenu of tools */}
                            {menuOpen && (
                                <Box sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.2,
                                    p: 1.5,
                                    borderRadius: '12px',
                                    bgcolor: 'rgba(28, 28, 28, 0.85)',
                                    backdropFilter: 'blur(15px)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
                                    alignItems: 'center'
                                }}>
                                    <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 'bold', fontSize: '0.65rem', letterSpacing: '0.05em' }}>
                                        TIPO DE MARCA
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, justifyContent: 'center', maxWidth: 140 }}>
                                        <Button
                                            variant={drawingTool === 'free' ? 'contained' : 'outlined'}
                                            onClick={() => selectTool('free')}
                                            sx={{
                                                minWidth: 40,
                                                height: 40,
                                                p: 0,
                                                borderRadius: '8px',
                                                bgcolor: drawingTool === 'free' ? '#ff1744' : 'transparent',
                                                color: '#fff',
                                                borderColor: '#ff1744',
                                                '&:hover': { bgcolor: drawingTool === 'free' ? '#d32f2f' : 'rgba(255,23,68,0.1)', borderColor: '#ff1744' }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faPaintBrush} style={{ fontSize: '0.9rem' }} />
                                        </Button>
                                        <Button
                                            variant={drawingTool === 'circle' ? 'contained' : 'outlined'}
                                            onClick={() => selectTool('circle')}
                                            sx={{
                                                minWidth: 40,
                                                height: 40,
                                                p: 0,
                                                borderRadius: '8px',
                                                bgcolor: drawingTool === 'circle' ? '#ff1744' : 'transparent',
                                                color: '#fff',
                                                borderColor: '#ff1744',
                                                '&:hover': { bgcolor: drawingTool === 'circle' ? '#d32f2f' : 'rgba(255,23,68,0.1)', borderColor: '#ff1744' }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faCircle} style={{ fontSize: '0.9rem' }} />
                                        </Button>
                                        <Button
                                            variant={drawingTool === 'rect' ? 'contained' : 'outlined'}
                                            onClick={() => selectTool('rect')}
                                            sx={{
                                                minWidth: 40,
                                                height: 40,
                                                p: 0,
                                                borderRadius: '8px',
                                                bgcolor: drawingTool === 'rect' ? '#ff1744' : 'transparent',
                                                color: '#fff',
                                                borderColor: '#ff1744',
                                                '&:hover': { bgcolor: drawingTool === 'rect' ? '#d32f2f' : 'rgba(255,23,68,0.1)', borderColor: '#ff1744' }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faSquare} style={{ fontSize: '0.9rem' }} />
                                        </Button>
                                        <Button
                                            variant={drawingTool === 'arrow' ? 'contained' : 'outlined'}
                                            onClick={() => selectTool('arrow')}
                                            sx={{
                                                minWidth: 40,
                                                height: 40,
                                                p: 0,
                                                borderRadius: '8px',
                                                bgcolor: drawingTool === 'arrow' ? '#ff1744' : 'transparent',
                                                color: '#fff',
                                                borderColor: '#ff1744',
                                                '&:hover': { bgcolor: drawingTool === 'arrow' ? '#d32f2f' : 'rgba(255,23,68,0.1)', borderColor: '#ff1744' }
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faArrowRight} style={{ fontSize: '0.9rem', transform: 'rotate(-45deg)' }} />
                                        </Button>
                                    </Box>

                                    <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 'bold', fontSize: '0.65rem', mt: 0.5, letterSpacing: '0.05em' }}>
                                        ACCIONES
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 0.8 }}>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                if (activeShape) {
                                                    setActiveShape(null)
                                                    const canvas = canvasRef.current
                                                    if (canvas) {
                                                        const ctx = canvas.getContext('2d')
                                                        ctx.putImageData(history[history.length - 1], 0, 0)
                                                    }
                                                } else {
                                                    handleUndo()
                                                }
                                            }}
                                            disabled={history.length <= 1 && !activeShape}
                                            sx={{
                                                height: 32,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                color: '#ff1744',
                                                borderColor: '#ff1744',
                                                '&:hover': { borderColor: '#d32f2f', bgcolor: 'rgba(255,23,68,0.1)' },
                                                '&.Mui-disabled': { borderColor: '#444', color: '#555' }
                                            }}
                                        >
                                            Deshacer
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={() => {
                                                setActiveShape(null)
                                                handleResetCanvas()
                                            }}
                                            sx={{
                                                height: 32,
                                                fontSize: '0.75rem',
                                                textTransform: 'none',
                                                borderRadius: '6px',
                                                color: '#ccc',
                                                borderColor: '#555',
                                                '&:hover': { borderColor: '#888', bgcolor: 'rgba(255,255,255,0.05)' }
                                            }}
                                        >
                                            Limpiar
                                        </Button>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        {/* Floating Comments and Save Panel (Bottom Center) */}
                        <Box sx={{
                            position: 'absolute',
                            bottom: '20px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 'calc(100% - 30px)',
                            maxWidth: { xs: '500px', md: '650px' },
                            zIndex: 5,
                            p: 2,
                            borderRadius: '16px',
                            bgcolor: 'rgba(28, 28, 28, 0.85)',
                            backdropFilter: 'blur(20px)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            boxShadow: '0 12px 40px rgba(0,0,0,0.6)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            boxSizing: 'border-box'
                        }}>
                            <Box sx={{ flexGrow: 1, width: '100%' }}>
                                <TextField
                                    multiline
                                    rows={1}
                                    maxRows={3}
                                    value={imageComment}
                                    onChange={(e) => setImageComment(e.target.value)}
                                    placeholder="Escribe un comentario específico para esta foto..."
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            color: '#fff',
                                            bgcolor: 'rgba(255,255,255,0.05)',
                                            fontSize: '0.85rem',
                                            borderRadius: '8px',
                                            '& fieldset': { borderColor: '#444' },
                                            '&:hover fieldset': { borderColor: '#ff1744' },
                                            '&.Mui-focused fieldset': { borderColor: '#ff1744' }
                                        }
                                    }}
                                />
                            </Box>
                            <Box sx={{ width: '100%' }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveEditedImage}
                                    fullWidth
                                    sx={{
                                        bgcolor: '#2e7d32',
                                        color: '#fff',
                                        fontWeight: 'bold',
                                        py: 1.2,
                                        borderRadius: '8px',
                                        textTransform: 'none',
                                        fontSize: '0.85rem',
                                        whiteSpace: 'nowrap',
                                        '&:hover': { bgcolor: '#1b5e20' }
                                    }}
                                >
                                    Guardar Imagen
                                </Button>
                            </Box>
                        </Box>
                    </Box>
                ) : (
                    // WEBCAM STREAM VIEW (RIGHT-SIDE CONTROL PANEL)
                    <Box sx={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        <Webcam
                            ref={webcamRef}
                            audio={false}
                            screenshotFormat="image/jpeg"
                            videoConstraints={videoConstraints}
                            style={{
                                height: '100vh',
                                width: '100vw',
                                objectFit: 'contain'
                            }}
                        />

                        {/* Top-Left back button */}
                        <IconButton
                            style={{
                                position: 'absolute',
                                left: '15px',
                                top: '15px',
                                zIndex: 10,
                                backgroundColor: 'rgba(0,0,0,0.5)'
                            }}
                            onClick={handleClose}
                        >
                            <FontAwesomeIcon icon={faArrowLeft} color="#ffffff" />
                        </IconButton>

                        {/* Right-Side Control Panel (Flash + Camera) */}
                        <Box sx={{
                            position: 'absolute',
                            right: '30px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 3
                        }}>
                            {/* Flash Button */}
                            <IconButton
                                onClick={toggleTorch}
                                sx={{
                                    backgroundColor: 'rgba(0,0,0,0.5)',
                                    color: torchOn ? '#ffeb3b' : '#ffffff',
                                    width: 48,
                                    height: 48,
                                    boxShadow: '0 4px 10px rgba(0,0,0,0.3)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.7)'
                                    }
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faBolt}
                                />
                            </IconButton>

                            {/* Camera Capture Button */}
                            <IconButton
                                onClick={handleCapture}
                                sx={{
                                    backgroundColor: 'rgba(0,0,0,0.6)',
                                    border: '2px solid #ffffff',
                                    p: 2,
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.4)',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0,0,0,0.8)'
                                    }
                                }}
                            >
                                <span style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FontAwesomeIcon
                                        icon={faCircleNotch}
                                        color="#ff1744"
                                        style={{ fontSize: '3.5rem' }}
                                    />
                                    <FontAwesomeIcon
                                        icon={faCamera}
                                        color="white"
                                        style={{
                                            fontSize: '1.5rem',
                                            position: 'absolute'
                                        }}
                                    />
                                </span>
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Box>
        </Modal>
    )
}

export default CameraModal
